import React from 'react';
import clsx from 'clsx';

export interface TableColumn {
  key: string;
  label: string;
  align?: 'left' | 'center' | 'right';
  className?: string;
}

export interface TableRow {
  [key: string]: React.ReactNode;
}

// Base Table Props
export interface BaseTableProps extends React.HTMLAttributes<HTMLTableElement> {
  /**
   * Table size variant
   */
  size?: 'sm' | 'base' | 'lg';
  
  /**
   * Whether to show striped rows
   */
  striped?: boolean;
  
  /**
   * Whether to show hover effects on rows
   */
  hoverable?: boolean;
}

// Table with data/columns props (original functionality)
export interface TableWithDataProps extends BaseTableProps {
  /**
   * Table columns configuration
   */
  columns: TableColumn[];
  
  /**
   * Table data rows
   */
  data: TableRow[];
  
  /**
   * Custom row className function
   */
  rowClassName?: (row: TableRow, index: number) => string;
  
  /**
   * Custom cell className function
   */
  cellClassName?: (value: React.ReactNode, column: TableColumn, row: TableRow, index: number) => string;
  
  onRowClicked?: (index: number) => void;
}

// Table without data/columns (for manual composition)
export interface TableWithoutDataProps extends BaseTableProps {
  columns?: never;
  data?: never;
  rowClassName?: never;
  cellClassName?: never;
  onRowClicked?: never;
  children: React.ReactNode;
}

export type TableProps = TableWithDataProps | TableWithoutDataProps;

// TableHead component
export interface TableHeadProps extends React.HTMLAttributes<HTMLTableSectionElement> {
  children?: React.ReactNode;
}

export const TableHead = React.forwardRef<HTMLTableSectionElement, TableHeadProps>(
  ({ children, className, ...props }, ref) => {
    return (
      <thead
        ref={ref}
        className={clsx('prometheus-table__head', className)}
        {...props}
      >
        {children}
      </thead>
    );
  }
);

TableHead.displayName = 'TableHead';

// TableBody component
export interface TableBodyProps extends React.HTMLAttributes<HTMLTableSectionElement> {
  children?: React.ReactNode;
}

export const TableBody = React.forwardRef<HTMLTableSectionElement, TableBodyProps>(
  ({ children, className, ...props }, ref) => {
    return (
      <tbody
        ref={ref}
        className={clsx('prometheus-table__body', className)}
        {...props}
      >
        {children}
      </tbody>
    );
  }
);

TableBody.displayName = 'TableBody';

// TableRow component
export interface TableRowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  children?: React.ReactNode;
  variant?: 'head' | 'body';
}

export const TableRow = React.forwardRef<HTMLTableRowElement, TableRowProps>(
  ({ children, className, variant = 'body', ...props }, ref) => {
    return (
      <tr
        ref={ref}
        className={clsx(
          variant === 'head' ? 'prometheus-table__head-row' : 'prometheus-table__body-row',
          className
        )}
        {...props}
      >
        {children}
      </tr>
    );
  }
);

TableRow.displayName = 'TableRow';

// TableCell component
export interface TableCellProps extends React.HTMLAttributes<HTMLTableCellElement> {
  children?: React.ReactNode;
  align?: 'left' | 'center' | 'right';
  variant?: 'head' | 'body';
  as?: 'th' | 'td';
  colSpan?: number;
}

export const TableCell = React.forwardRef<HTMLTableCellElement, TableCellProps>(
  ({ children, className, align = 'left', variant = 'body', as, ...props }, ref) => {
    const Component = as || (variant === 'head' ? 'th' : 'td');
    const cellClass = variant === 'head' ? 'prometheus-table__head-cell' : 'prometheus-table__body-cell';
    const alignClass = `${cellClass}--align-${align}`;

    return (
      <Component
        ref={ref}
        className={clsx(cellClass, alignClass, className)}
        {...props}
      >
        {children}
      </Component>
    );
  }
);

TableCell.displayName = 'TableCell';

/**
 * Table component for displaying tabular data with Prometheus styling
 * Can be used with data/columns props for automatic rendering, or with children for manual composition
 */
export const Table = React.forwardRef<HTMLTableElement, TableProps>(
  (props, ref) => {
    const {
      size = 'base',
      striped = false,
      hoverable = true,
      className,
      ...restProps
    } = props;

    const tableClassName = clsx(
      'prometheus-table',
      `prometheus-table--size-${size}`,
      {
        'prometheus-table--striped': striped,
        'prometheus-table--hoverable': hoverable,
      },
      className
    );

    // Check if this is the data-driven version
    if ('columns' in props && 'data' in props) {
      const {
        columns,
        data,
        rowClassName,
        cellClassName,
        onRowClicked,
        ...tableProps
      } = restProps as Omit<TableWithDataProps, keyof BaseTableProps>;

      return (
        <div className="prometheus-table-scroll">
          <table
            ref={ref}
            className={tableClassName}
            {...tableProps}
          >
            <TableHead>
              <TableRow variant="head">
                {columns.map((column) => (
                  <TableCell
                    key={column.key}
                    variant="head"
                    align={column.align}
                    className={column.className}
                  >
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((row, rowIndex) => (
                <TableRow
                  key={rowIndex}
                  className={rowClassName?.(row, rowIndex)}
                  onClick={onRowClicked ? () => onRowClicked(rowIndex) : undefined}
                >
                  {columns.map((column) => {
                    const value = row[column.key];
                    return (
                      <TableCell
                        key={column.key}
                        align={column.align}
                        className={cellClassName?.(value, column, row, rowIndex)}
                      >
                        {value}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </table>
        </div>
      );
    }

    // Manual composition version
    const { children, ...tableProps } = restProps as TableWithoutDataProps;

    return (
      <div className="prometheus-table-scroll">
        <table
          ref={ref}
          className={tableClassName}
          {...tableProps}
        >
          {children}
        </table>
      </div>
    );
  }
);

Table.displayName = 'Table';

export default Table;